import { ReactNode, useEffect } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '@/hooks/useAuth';
import { DevAuthProvider } from '@/hooks/useDevAuth';
import { CartProvider } from '@/hooks/useCart';
import { SubscriptionProvider } from '@/hooks/useSubscription';
import { DegradedModeProvider } from '@/contexts/DegradedModeContext';
import { MaintenanceMode } from '@/components/guards/MaintenanceMode';
import { NetworkStatusBanner } from '@/components/guards/NetworkStatusBanner';
import { DegradedModeBanner } from '@/components/guards/DegradedModeBanner';
import { TrackingEvents } from '@/components/tracking/TrackingEvents';
import { ScrollToTop } from '@/components/ScrollToTop';
import { ErrorBoundaryEnhanced } from '@/components/ErrorBoundaryEnhanced';

/**
 * AppProviders - Centralized provider hierarchy
 * 
 * ORDER IS CRITICAL - Do not modify without testing:
 * 
 * 1. QueryClientProvider - Must be first for React Query to work
 * 2. DegradedModeProvider - Independent of auth, manages app degradation
 * 3. TooltipProvider - Global UI tooltips
 * 4. BrowserRouter - Routing (before components using useLocation/useNavigate)
 * 5. MaintenanceMode - Circuit breaker (fail-open on error)
 * 6. AuthProvider - Authentication (needs Router for useNavigate)
 * 7. DevAuthProvider - Development auth overrides
 * 8. SubscriptionProvider - Depends on AuthProvider
 * 9. CartProvider - Depends on SubscriptionProvider
 * 10. TrackingEvents - Analytics tracking
 * 11. ErrorBoundaryEnhanced - Global error boundary
 * 
 * @see docs/ARCHITECTURE.md for detailed provider documentation
 */

// Configuration optimisée du QueryClient pour la performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - garde les données fraîches
      gcTime: 10 * 60 * 1000, // 10 minutes - garde en cache
      retry: 1, // Moins de tentatives pour une réponse plus rapide
      refetchOnWindowFocus: false, // Évite les requêtes inutiles
    },
  },
});

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <DegradedModeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <MaintenanceMode>
              <NetworkStatusBanner />
              <DegradedModeBanner />
              <AuthProvider>
                <DevAuthProvider>
                  <SubscriptionProvider>
                    <CartProvider>
                      <TrackingEvents>
                        <ErrorBoundaryEnhanced featureId="global-app" maxRetries={2}>
                          {children}
                        </ErrorBoundaryEnhanced>
                      </TrackingEvents>
                    </CartProvider>
                  </SubscriptionProvider>
                </DevAuthProvider>
              </AuthProvider>
            </MaintenanceMode>
          </BrowserRouter>
        </TooltipProvider>
      </DegradedModeProvider>
    </QueryClientProvider>
  );
}

export { queryClient };
